import React, { useEffect, useState, useRef, useMemo } from "react";
import Prismic from "@prismicio/client";
import "../App.css";
import Header from "./headernew";
import DumpardDiv from "./dumpyard-div";
import Contact from "./contact";
import Work from "./MainWork";
import OtherWork from "./OtherWork";
import { Fade } from "react-awesome-reveal";
import WorkedWith from "./Expertise";
import SkillsMarquee from "./SkillsMarquee";
import PixelIcon from "./PixelIcon";
import StickyCTA from "./stickyCTA";
import MobileMenu from "./menu";
import SEO from "./SEO";

// Same slot-machine digit reel as the case-study metrics / About page
// highlights — duplicated here per this codebase's convention of not
// sharing small components across files. Always resting on the correct
// digit even before it spins, so there's no blank state if it never fires.
function JackpotDigit({ digit, animate, delayMs }) {
  const targetIndex = Number(digit);

  const sequence = useMemo(() => {
    const seq = [];
    const loops = 2;
    for (let l = 0; l < loops; l += 1) {
      for (let d = 0; d <= 9; d += 1) seq.push(d);
    }
    seq.push(targetIndex);
    return seq;
  }, [targetIndex]);

  const restPosition = targetIndex;
  const landedPosition = sequence.length - 1;
  const position = animate ? landedPosition : restPosition;

  return (
    <span className="jackpot-digit">
      <span
        className="jackpot-digit-strip"
        style={{
          transform: `translateY(-${position}em)`,
          transitionDelay: animate ? `${delayMs}ms` : "0ms",
        }}
      >
        {sequence.map((d, i) => (
          <span className="jackpot-digit-cell" key={i}>
            {d}
          </span>
        ))}
      </span>
    </span>
  );
}

// Parses a metric like "1M+", "₹120 Cr+" into prefix/number/suffix by
// finding the first digit run — only that number spins.
function AnimatedMetricValue({ value, animate }) {
  const parsed = useMemo(() => {
    if (typeof value !== "string") return null;
    const match = value.match(/[\d,.]*\d/);
    if (!match) return null;
    const numberPart = match[0];
    const prefix = value.slice(0, match.index);
    const suffix = value.slice(match.index + numberPart.length);
    return { prefix, numberPart, suffix };
  }, [value]);

  if (!parsed) {
    return <>{value}</>;
  }

  const { prefix, numberPart, suffix } = parsed;
  let digitCount = 0;

  return (
    <span className="jackpot-value">
      {prefix && <span className="jackpot-static">{prefix}</span>}
      {numberPart.split("").map((char, i) => {
        if (!/\d/.test(char)) {
          return (
            <span className="jackpot-static" key={i}>
              {char}
            </span>
          );
        }
        const delayMs = digitCount * 60;
        digitCount += 1;
        return (
          <JackpotDigit key={i} digit={char} animate={animate} delayMs={delayMs} />
        );
      })}
      {suffix && <span className="jackpot-static">{suffix}</span>}
    </span>
  );
}

function Main() {
  // Fetched once here and passed down to both Header and OtherWork, which both
  // used to independently fetch the same "morework" documents (and OtherWork
  // was even fetching it twice on its own) — tripling this request on load.
  // null (not []) while the fetch is in flight, so children can tell "still
  // loading" apart from "loaded, but there's nothing" and show a skeleton.
  const [moreWorkDoc, setMoreWorkDoc] = useState(null);
  // Fetched purely so Header can compare both pools and show whichever
  // case study actually has the most recent publish date — MainWork.jsx
  // still does its own separate worklist fetch for rendering the list.
  const [mainWorkDoc, setMainWorkDoc] = useState(null);

  useEffect(() => {
    const apiEndpoint = "https://harshitsharma.prismic.io/api/v2";
    const accessToken = process.env.REACT_APP_PRISMIC_TOKEN;
    const Client = Prismic.client(apiEndpoint, { accessToken });

    const fetchWork = async () => {
      const [moreRes, mainRes] = await Promise.all([
        Client.query(Prismic.Predicates.at("document.type", "morework"), {
          orderings: "[my.morework.order desc]",
        }),
        Client.query(Prismic.Predicates.at("document.type", "worklist"), {
          orderings: "[my.worklist.order desc]",
        }),
      ]);
      if (moreRes) setMoreWorkDoc(moreRes.results);
      if (mainRes) setMainWorkDoc(mainRes.results);
    };

    fetchWork();
  }, []);

  // Highlight numbers re-trigger their count-up on every fresh entry into
  // view, from either direction — same pattern as the case-study metrics
  // and the About page's highlight cards.
  const highlightsRef = useRef(null);
  const [highlightsInView, setHighlightsInView] = useState(false);

  useEffect(() => {
    const handleHighlightsScroll = () => {
      if (!highlightsRef.current) return;
      const rect = highlightsRef.current.getBoundingClientRect();
      const inViewNow = rect.top < window.innerHeight * 0.9 && rect.bottom > 0;
      setHighlightsInView(inViewNow);
    };

    handleHighlightsScroll();
    window.addEventListener("scroll", handleHighlightsScroll);
    return () => window.removeEventListener("scroll", handleHighlightsScroll);
  }, []);

  return (
    <div className="App">
      <SEO path="/" />

      <div id="hero-header">
        <Header doc={moreWorkDoc} mainWorkDoc={mainWorkDoc} />
      </div>

      <section id="work">
        <Work />
        <OtherWork doc={moreWorkDoc} />
      </section>

      <section
        id="dumpyard"
        style={{
          backgroundImage:
            "linear-gradient(0deg, rgb(0 87 255 / 13%), rgb(0 87 255 / 0%));",
        }}
      >
        <DumpardDiv />
      </section>
      <SkillsMarquee />

      <section className="home-highlights-section" ref={highlightsRef}>
        <div className="home-highlights-grid-row">
          {Array.from({ length: 16 }).map((_, i) => (
            <div className="home-highlights-grid-cell" key={i} />
          ))}
        </div>

        <div className="home-highlights-row">
          <div className="home-highlight-card">
            <span className="home-highlight-value">
              <AnimatedMetricValue value="1M+" animate={highlightsInView} />
            </span>
            <div className="home-highlight-meta">
              <PixelIcon type="globe" color="var(--accent-blue)" className="home-highlight-icon" />
              <span className="home-highlight-label">
                User
                <br />
                Reach
              </span>
            </div>
          </div>

          <div className="home-highlight-card">
            <span className="home-highlight-value">
              <AnimatedMetricValue value="₹120 Cr+" animate={highlightsInView} />
            </span>
            <div className="home-highlight-meta">
              <PixelIcon type="globe" color="var(--accent-blue)" className="home-highlight-icon" />
              <span className="home-highlight-label">
                Revenue
                <br />
                Impacted
              </span>
            </div>
          </div>

          <div className="home-highlight-card">
            <span className="home-highlight-value">
              <AnimatedMetricValue value="30% Faster" animate={highlightsInView} />
            </span>
            <div className="home-highlight-meta">
              <PixelIcon type="globe" color="var(--accent-blue)" className="home-highlight-icon" />
              <span className="home-highlight-label">
                Go-To-
                <br />
                Market
              </span>
            </div>
          </div>
        </div>

        <div className="home-highlights-grid-row">
          {Array.from({ length: 16 }).map((_, i) => (
            <div className="home-highlights-grid-cell" key={i} />
          ))}
        </div>
      </section>

      <WorkedWith />
      <Fade delay={20} duration={600} triggerOnce>
        <section id="contact">
          <Contact />
        </section>
      </Fade>
      <StickyCTA />
      <MobileMenu/>
     
    </div>
  );
}

export default Main;
